import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,  
  Stack,
  useTheme,
  alpha,
  useMediaQuery,
} from "@mui/material";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Tooltip as RadarTooltip,
  PieChart,
  Pie,
  Cell,
  Label,
} from "recharts";
import { formatDecimal } from "../../utils/numberFormat";

// Dados fonte: métricas e grupos de donuts
const sourceData = {

 metrics: [
   { name: "Retail quality",          limit: 25 },
   { name: "Traffic safety",          limit: 5 },
   { name: "Land use-mix",            limit: 25 },
   { name: "Intersection density",    limit: 25 },
   { name: "Population density",      limit: 25 },
   { name: "Access to amenities",     limit: 70 },
   { name: "Outdoor air quality",     limit: 10 },
   { name: "Access to green space",   limit: 10 },
   { name: "Urban crime rate",        limit: 5 },
  ],
  // Cada donut normaliza usando este "max"
  donutGroups: [
    { key: "total", label: "General", max: 200 },
    {
      key: "postProjectWalkabilityAndCyclabilityCombined",
      label: "Mobility",
      max: 100,
    },
    { key: "postProjectLivabilityCombined", label: "Livability", max: 100 },
  ],
};

// Helpers de cor
function hexToRgba(hex, alpha = 1) {
  let h = hex.replace("#", "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function rgbaWithAlpha(color, alpha = 1) {
  // Se já vier rgba(...)
  const rgbaMatch = color.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbaMatch) {
    const parts = rgbaMatch[1].split(",").map((s) => s.trim());
    const [r, g, b] = parts;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  // Se vier em hex (#rrggbb ou #rgb)
  if (color.startsWith("#")) {
    return hexToRgba(color, alpha);
  }
  // Fallback: devolve a mesma cor (sem alpha)
  return color;
}

const toIndicatorDiffs = (projectData) => {
  // Fun helper: usa difference se existir; senão calcula post - pre
  /*const diffOrCalc = (diffKey, preKey, postKey) => {
    if (diffKey in projectData && typeof projectData[diffKey] === "number") {
      return projectData[diffKey];
    }
    if (preKey in projectData && postKey in projectData) {
      const pre = projectData[preKey];
      const post = projectData[postKey];
      return typeof pre === "number" && typeof post === "number"
        ? post - pre
        : null;
    }
    return null;
  };*/

  return {
    "Retail quality": projectData.postProjectRetailFloorAreaRatio /*diffOrCalc(
      "differenceRetailFloorAreaRatio",
      "preProjectRetailFloorAreaRatio",
      "postProjectRetailFloorAreaRatio"
    )*/,
    "Traffic safety": projectData.postProjectTrafficSafety/*diffOrCalc(
      "differenceProjectTrafficSafety",
      "preProjectTrafficSafety",
      "postProjectTrafficSafety"
    )*/,
    "Land use-mix": projectData.postProjectLandUseMix/*diffOrCalc(
      "differenceLandUseMix",
      "preProjectLandUseMix",
      "postProjectLandUseMix"
    )*/,
    "Intersection density": projectData.postProjectStreetConnectivity/*diffOrCalc(
      "differenceStreetConnectivity",
      "preProjectStreetConnectivity",
      "postProjectStreetConnectivity"
    )*/,
    "Population density": projectData.postProjectDwellingResidentialDensity/*diffOrCalc(
      "differenceDwellingDensity",
      "preProjectDwellingResidentialDensity",
      "postProjectDwellingResidentialDensity"
    )*/,
    "Access to amenities": projectData.postProjectServices/* diffOrCalc(
      "differenceServices",
      "preProjectServices",
      "postProjectServices"
    )*/,
    "Outdoor air quality": projectData.postProjectEnvironment/*diffOrCalc(
      "differenceEnvironment",
      "preProjectEnvironment",
      "postProjectEnvironment"
    )*/,
    "Access to green space": projectData.postProjectNaturalArea /* diffOrCalc(
      "differenceProjectNaturalArea",
      "preProjectNaturalArea",
      "postProjectNaturalArea"
    )*/,
    // crime_diff = (1 - postSafety) - (1 - preSafety) = preSafety - postSafety = -differenceSafety
    "Urban crime rate": projectData.postProjectSafety
      /*"differenceSafety" in projectData &&
      typeof projectData.differenceSafety === "number"
        ? -projectData.differenceSafety
        : "preProjectSafety" in projectData &&
          "postProjectSafety" in projectData
        ? projectData.preProjectSafety - projectData.postProjectSafety
        : null,*/
  };
};

/* =========================================================
   CONSTANTES / UTILITÁRIOS
   ========================================================= */
// Paleta focada em 3 projetos
const palette = ["#7C3AED", "#22C55E", "#3B82F6"]; // roxo, verde, azul
const colorForIndex = (i) => palette[i % palette.length];
const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const getRawMetricKey = (projectName) => `${projectName}__raw`;

function buildRadarData(metrics, diffsArray) {

  //console.log("Building radar data with metrics=", metrics, " diffsArray=", diffsArray);

  if (!Array.isArray(diffsArray)) return [];

  return metrics.map((metric) => {
    const row = { metric: metric.name, limit: metric.limit };
    diffsArray.forEach((item) => {
      const projectName = item.projectName ?? item.name ?? "(sem nome)";
      const rawValue = typeof item[metric.name] === "number" ? item[metric.name] : 0;
      const percent = (rawValue / metric.limit) * 100;
      row[projectName] = percent;
      row[getRawMetricKey(projectName)] = rawValue;
      //row[projectName] = typeof item[metric] === "number" ? item[metric] : 0;
      //console.log("Metric=", metric, " Project=", projectName, " Value=", row[projectName]);
    });
    //console.log("Radar row for metric=", metric.name, " row=", row);
    return row;
  });
}

// Onde vamos buscar os valores por key (ajusta a origem do "total" em baixo)
function getDonutValue(project, key) {
  if (key === "total") {
    return (
      project.projectInfo?.total ?? project.total ?? project.values?.total ?? 0
    );
  }
  return (
    project.projectInfo?.[key] ?? project.values?.[key] ?? project[key] ?? 0
  );
}

/* =========================================================
   COMPONENTE: Donut multi-projeto (anéis concêntricos)
   ========================================================= */
function MultiProjectDonut({
  title,
  projects,
  size = 180,
  thickness = 12,
  gap = 8,
  reduceMotion = false,
}) {
  const rings = projects.map((p, i) => {
    const item =
      p.donuts.find(
        (item) => item.label.toLowerCase() === title.toLowerCase()
      ) ?? null;
    const percent = clamp((item.value / item.max) * 100, 0, 100);
     
    //console.log("Donut ring for Project=", p.projectName, " donut=", p.donuts);
    //console.log(`Donut ${title} - Project ${p.projectName}, percent=${percent.toFixed(2)}% item value=`, item.value + " title=", title);
    
    return {
      name: p.projectName,
      id: p.actionSimulationId,
      color: p.color,
      value: item.value,
      percent,
    };
  });

  const center = size / 2;
  const count = Math.max(rings.length, 1);
  const totalThickness = count * thickness + (count - 1) * gap;
  const baseInner = Math.max(26, center - totalThickness - 12);

  return (
    <Box position="relative" width={size} height={size}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ textDecoration:"underline"}}>
          {title}
        </Typography>
      </Box>

      <PieChart width={size} height={size}>
        {rings.map((r, i) => {
          //console.log(`Drawing ring ${i} with percent=${r.percent}% for project=`, r );
          const inner = baseInner + i * (thickness + gap);
          const outer = (inner + thickness);
          const restColor = rgbaWithAlpha(r.color, 0.08);
          const fillColor = rgbaWithAlpha(r.color, 0.4);

          const data = [
            {
              id: r.id,
              name: r.name,
              value: r.percent,
              color: fillColor,
              isValue: true,
            },
            {
              id: r.id,
              name: "rest",
              value: 100 - r.percent,
              color: restColor,
              isValue: false,
            },
          ];
          return (
            <Pie
              key={r.id}
              data={data}
              cx={center}
              cy={center}
              innerRadius={inner}
              outerRadius={outer}
              startAngle={90}
              endAngle={-270}
              paddingAngle={0}
              stroke={"none"}
              dataKey="value"
              isAnimationActive={!reduceMotion}
              animationBegin={120 + i * 120}
              animationDuration={800}
              animationEasing="ease-out">
              {data.map((d, idx) => (
                <Cell
                  key={idx}
                  fill={d.color}
                  stroke={d.isValue ? fillColor : "none"}
                  strokeWidth={d.isValue ? 2 : 0}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              ))}

              <Label
                content={({ viewBox }) => {
                  if (
                    !viewBox ||
                    typeof viewBox.cx !== "number" ||
                    typeof viewBox.cy !== "number"
                  )
                    return null;
                  const { cx, cy } = viewBox;
                  return (
                    <text
                      x={cx}
                      y={cy - outer + 12} // posiciona próximo do topo do círculo
                      textAnchor="end"
                      dominantBaseline="middle"
                      fill={r.color || "#37D07E"}
                      style={{ fontWeight: 700, fontSize: 14 }}>
                      {formatDecimal(r.value, 0, 2)}
                    </text>
                  );
                }}
              />
            </Pie>
          );
        })}
      </PieChart>
    </Box>
  );
}

/* =========================================================
   COMPONENTE PRINCIPAL
   ========================================================= */
export default function SimulationInitialCharts({ projects, forPdf = false }) {
  const theme = useTheme();
  const bgFill =
    theme.palette.mode === "dark"
      ? alpha("#ffffff", 0.03) // cinza claro no dark
      : alpha("#000000", 0.03); // cinza claro no light

  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const { metrics, donutGroups } = sourceData;
  
  const safeName = (name) =>
    typeof name === "string" && name.trim() !== "" ? name.trim() : "No name defined";

  const resolveProjectName = (project) =>
    safeName(
      project?.projectName ??
        project?.name ??
        project?.projectInfo?.projectName ??
        project?.projectInfo?.name
    );

  const normalizedProjects = React.useMemo(
    () =>
      (projects || []).map((p) => ({
        ...p,
        projectName: resolveProjectName(p),
      })),
    [projects]
  );

  // Seleção inicial: até 3 projetos
  const [selectedIds, setSelectedIds] = useState(
    normalizedProjects.map((p) => p.actionSimulationId)
  );

  // Set com os ids selecionados (rápido para contains)
  const selectedSet = React.useMemo(
    () => new Set(selectedIds),
    [selectedIds]
  );

  const diffsArray = normalizedProjects.map((p) => ({
    projectName: p.projectName, // ?? p.name ?? "(sem nome)",
    actionSimulationId: p.actionSimulationId,
    ...toIndicatorDiffs(p.projectInfo),
  }));
  //console.log("diffsArray=", diffsArray);
  
  const filteredDiffsArray = React.useMemo(
    () => (selectedIds.length ? diffsArray.filter(d => selectedSet.has(d.actionSimulationId)) : diffsArray),
    [diffsArray, selectedIds.length, selectedSet]
  );

  const radarData = useMemo(
    () => buildRadarData(metrics, filteredDiffsArray),
    [metrics, diffsArray]
  );

  // Tamanhos responsivos do Radar
  const radarSize = isMdDown ? 420 : 680;
  const radarOuterRadius = isMdDown ? 135 : 180;
  const radarCenter = radarSize / 2;

  // 2) Agora acrescentas as chaves do donutGroups a cada elemento
  const diffsWithDonuts = normalizedProjects.map((p, idx) => {
    const base = diffsArray[idx] ?? {
      projectName: p.projectName,
      actionSimulationId: p.actionSimulationId
    };

    // Total pedido: soma dos dois diffs
    const postMobility = p.projectInfo?.postProjectWalkabilityAndCyclabilityCombined ?? 0;
    const postLivability = p.projectInfo?.postProjectLivabilityCombined ?? 0;
    const total = postMobility + postLivability;
    //console.log("Checkng values for Project=", p.projectName, " values=", p.projectInfo, " total=", total);

    // Construir a lista de donuts com label, value, max
    const donuts = donutGroups.map(({ key, label, max }) => ({
      key,
      label,
      value: key === "total"? total : getDonutValue(p, key),
      max,
    }));

    //console.log("Project=", p.projectName, " donuts=", donuts);

    return {
      projectName: p.projectName,
      actionSimulationId: p.actionSimulationId,
      color: colorForIndex(idx),
      donuts, // [{key,label,value,max}, ...]      
    };
  });

  //console.log("diffsWithDonuts=", diffsWithDonuts, "selectedSet=", selectedSet);

  // Para os Donuts (o teu JSON com donuts por projeto)
  const filteredDiffsWithDonuts = React.useMemo(
    () => (selectedIds.length ? diffsWithDonuts.filter(d => selectedSet.has(d.actionSimulationId)) : diffsWithDonuts),
    [diffsWithDonuts, selectedIds.length, selectedSet]
  );

  //console.log("filteredDiffsWithDonuts=", filteredDiffsWithDonuts);

  return (
    <Box
      sx={{ 
        display: "flex", 
        flexDirection: "column", 
        gap: 1, 
        width: "100%", 
        mb: 6,
        ...(forPdf && { transform: "scale(0.9)", transformOrigin: "top left", width: "calc(100% / 0.9)", mb:0, gap: 0 })
      }}>
      {/* Linha superior: Radar (esq) + Donuts (dir) */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          gap: 0,
          width: "100%",
        }}>
        {/* Coluna do Radar */}
        <Box
          sx={{
            flex: "1 1 0%",
            bgcolor: "background.paper",
            borderRadius: 1,
            px: 2, py: 0,
            display: "flex",
            flexDirection: "column",
          }}>
          {/* Wrapper do gráfico: assegura fluxo normal e espaço */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              pb: 0,
              display: "flex",
              justifyContent:  "center",
              alignItems: "center",
            }}>
            <RadarChart
              width={radarSize * 1.1}
              height={radarSize * 1}
              cx={radarCenter + 30}
              cy={radarCenter - 30}
              outerRadius={radarOuterRadius * 1.24}
              data={radarData}>
              <g pointerEvents="none">
                <circle
                  cx={radarCenter +  30}
                  cy={radarCenter - 30}
                  r={radarOuterRadius * 1.24}
                  fill={bgFill}
                  stroke="#f3f3ff"
                />
              </g>
              {/* Grelha circular para manter o look da imagem */}
              <PolarGrid
                gridType="circle"
                radialLines={true}
                stroke="#f3f3ff" // linhas brancas
                strokeOpacity={1}
                strokeWidth={3}
              />
              <PolarAngleAxis
                dataKey="metric"
                tick={{
                  fill: theme.palette.text.primary,
                  fontSize: 14, // mais pequeno
                  fontWeight: forPdf ? theme.typography.fontWeightMedium : theme.typography.fontWeightBold , // bold segundo o tema MUI
                }}
              />
              <PolarRadiusAxis angle={40} domain={[-2, 3]} tick={false} axisLine={false} tickLine={false}  />
              {/* era 0-100, agora é diferença (-100 a +100) */}
              <RadarTooltip 
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  return (
                    <Box
                      sx={{
                        bgcolor: "background.paper",
                        border: "1px solid #ccc",
                        borderRadius: 1,
                        p: 1.5,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {label}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {payload.map((entry, index) => {
                          const rowPayload = entry && entry.payload ? entry.payload : {};
                          const rawCandidate = rowPayload[getRawMetricKey(entry.name)];
                          const originalValue =
                            typeof rawCandidate === "number"
                              ? rawCandidate
                              : Number(entry && entry.value != null ? entry.value : 0);

                          return (
                            <Box key={index} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    bgcolor: entry.color,
                                    borderRadius: 0.5,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography variant="caption" sx={{ fontWeight: 500, color: entry.color }}>
                                  {entry.name}
                                </Typography>
                              </Box>
                              <Box
                                component="span"
                                sx={{ fontSize: 12, fontWeight: 500, color: entry.color }}>
                                {Number(originalValue).toFixed(2)}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  );
                }}
              />
              {/*<Legend
                wrapperStyle={{
                  position: "relative",
                  marginTop: 8,
                }}
              />*/}
              {normalizedProjects.map((p, i) => (
                <Radar
                  key={p.actionSimulationId}
                  name={p.projectName}
                  dataKey={p.projectName}
                  stroke={colorForIndex(i)}
                  fill={colorForIndex(i)}
                  fillOpacity={0.2}
                  dot={false}
                  isAnimationActive={!reduceMotion}
                  animationBegin={200}
                  animationDuration={900}
                  animationEasing="ease-out"
                />
              ))}
            </RadarChart>
          </Box>
        </Box>

        {/* Coluna dos Donuts */}
        <Box
          sx={{
            flex: "0 0 auto",
            width: { xs: "100%", md: 300 },
            bgcolor: "background.paper",
            borderRadius: 1,
            p: 2, px:0,
            alignSelf: "flex-start",
          }}>
          <Stack spacing={4} alignItems="center">
            {donutGroups.map((g) => (
              <Box key={g.key} textAlign="center">
                <MultiProjectDonut
                  title={g.label}
                  projects={filteredDiffsWithDonuts}
                  size={180}
                  thickness={14}
                  gap={0}
                  reduceMotion={reduceMotion}
                />
                {/* legenda opcional por projeto */}
                <Stack
                  direction="row"
                  spacing={1}
                  mt={1}
                  flexWrap="wrap"
                  justifyContent="center"></Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      {/* Barra de seleção por baixo dos gráficos */}
      <Box
        sx={{
          p: 2,
          bgcolor: "#f5f5f5",
          borderRadius: 1,
          display: "flex",
          justifyContent: "center",
          gap:  2,
          flexWrap: "wrap",
          position: "relative", // garante que segue o fluxo
          zIndex: "auto",
          maxWidth: "none",
          mx: "auto",
        }}>
        {normalizedProjects.map((p, i) => {
          const isSelected = selectedIds.includes(p.actionSimulationId);

          return (
            <Box
              key={p.actionSimulationId}
              onClick={() => {
                setSelectedIds((prev) =>
                  isSelected
                    ? prev.filter((id) => id !== p.actionSimulationId)
                    : [...prev, p.actionSimulationId]
                );
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 1,
                cursor: "pointer",
                bgcolor: isSelected ? `${colorForIndex(i)}20` : "transparent",
                border: `1px solid ${colorForIndex(i)}`,
                transition: "background 0.2s, transform 0.2s",
                "&:hover": {
                  bgcolor: isSelected ? `${colorForIndex(i)}30` : "#eee",
                  transform: "translateY(-1px)",
                },
              }}>
              {/* Quadrado colorido */}
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: colorForIndex(i),
                  borderRadius: 0.5,
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{ color: colorForIndex(i), fontWeight: 500 }}>
                {p.projectName}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}